import { func, node, object } from "prop-types";
import React, { useReducer } from "react";

import {
  getAmountInDollarsAndCents,
  getAmountInDollarsClean,
} from "Utilities/Amount/getAmountInDollarsClean";
import { formatTimestampToSQLDate } from "Utilities/Date/formatTimestampToSQLDate";

import recurringGiftsActions from "./actions";

const RecurringGiftsContext = React.createContext();

const editModalPanels = {
  initial: "initial",
  edit: "edit",
  paymentMethods: "paymentMethods",
};

const defaultGiftDetails = {
  userData: {},
  campaignData: {},
  fund: {},
};

const defaultCurrentGift = {
  paymentMethod: {},
};

const defaultState = {
  networkHasRecurringEnabled: false,
  recurringGiftsListStatus: null,
  recurringGifts: [],
  thirtyDayTotal: null,
  editModal: {
    isSubmitting: false,
    currentGift: defaultCurrentGift,
    giftWithEdits: defaultCurrentGift,
    addOnGifts: [],
    deletedAddOnGifts: [],
    giftDetails: defaultGiftDetails,
    paymentMethods: [],
    currentPanel: editModalPanels.initial,
    editPanelValidationError: null,
    giftHasBeenUpdated: false,
  },
  duplicatePaymentForTodayError: false,
  userID: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "handleGetRecurringGiftsResponse": {
      return {
        ...state,
        networkHasRecurringEnabled: true,
        recurringGifts: action.payload.recurringGifts,
        thirtyDayTotal: action.payload.thirtyDayTotal,
        userID: action.payload.userID,
        ctfData: action.payload.ctfData,
        profileUserID:
          action.payload.profileUserID || state.profileUserID || null,
      };
    }

    case "setRecurringGiftListStatus": {
      return {
        ...state,
        recurringGiftsListStatus: action.payload,
      };
    }

    case "setRecurringGifts": {
      return {
        ...state,
        recurringGifts: action.payload,
      };
    }

    case "setThirtyDayTotal": {
      return {
        ...state,
        thirtyDayTotal: action.payload,
      };
    }

    case "setCurrentGift": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          currentGift: { ...action.payload },
          giftWithEdits: composeGiftWithEditsFromCurrentGift(action.payload),
          addOnGifts: composeAddOnGifts(action.payload?.designations),
          mainGift: composeMainGift(action.payload?.designations),
        },
      };
    }

    case recurringGiftsActions.SET_MAIN_GIFT_AMOUNT: {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          mainGift: { ...state.editModal.mainGift, amount: action.payload },
        },
      };
    }

    case recurringGiftsActions.SET_ADD_ON_GIFTS: {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          addOnGifts: action.payload,
        },
      };
    }

    case recurringGiftsActions.ADD_DELETED_ADD_ON_GIFT: {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          deletedAddOnGifts: Array.from(
            new Set([
              ...(state.editModal.deletedAddOnGifts || []),
              action.payload,
            ])
          ),
        },
      };
    }

    case "setGiftDetails": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftDetails: action.payload,
        },
      };
    }

    case "clearGiftDetails": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftDetails: defaultGiftDetails,
          currentGift: defaultCurrentGift,
          giftWithEdits: defaultCurrentGift,
        },
      };
    }

    case "setGiftWithEdits": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: action.payload,
        },
      };
    }

    case "setCurrentPanel": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          currentPanel: action.payload,
        },
      };
    }

    case "setAmountInDollars": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            amountInDollars: action.payload,
          },
        },
      };
    }

    case "updateInterval": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            interval: action.payload,
          },
        },
      };
    }

    case "updateStartDate": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            startDate: action.payload,
          },
        },
      };
    }

    case "updateEndDate": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            endDate: action.payload,
          },
        },
      };
    }

    case "setApplyToExistingPledge": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            applyToExistingPledge: action.payload,
          },
        },
      };
    }

    case "resetGiftWithEdits": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: composeGiftWithEditsFromCurrentGift(
            state.editModal.currentGift
          ),
          addOnGifts: composeAddOnGifts(
            state.editModal.currentGift.designations
          ),
          mainGift: composeMainGift(state.editModal.currentGift.designations),
        },
      };
    }

    case "setIsSubmitting": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          isSubmitting: action.payload,
        },
      };
    }

    case "setEditPanelValidationError": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          editPanelValidationError: action.payload,
        },
      };
    }

    case "clearEditPanelValidationError": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          editPanelValidationError: null,
        },
      };
    }

    case "updateMemo": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          giftWithEdits: {
            ...state.editModal.giftWithEdits,
            memo: action.payload,
          },
        },
      };
    }

    case "setCurrentMemo": {
      return {
        ...state,
        editModal: {
          ...state.editModal,
          currentGift: {
            ...state.editModal.currentGift,
            memo: action.payload,
          },
        },
      };
    }

    case "setDuplicatePaymentForTodayError": {
      return {
        ...state,
        duplicatePaymentForTodayError: action.payload,
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

function RecurringGiftsProvider({
  children,
  initialState = defaultState,
  onSuccessfulSave = undefined,
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <RecurringGiftsContext.Provider
      value={{ state, dispatch, onSuccessfulSave }}
    >
      {children}
    </RecurringGiftsContext.Provider>
  );
}

RecurringGiftsProvider.propTypes = {
  children: node,
  initialState: object,
  onSuccessfulSave: func,
};

function useRecurringGifts() {
  const context = React.useContext(RecurringGiftsContext);
  if (context === undefined) {
    throw new Error(
      `useRecurringGifts must be used within a PaymentFormProvider`
    );
  }
  return context;
}

export {
  RecurringGiftsContext,
  RecurringGiftsProvider,
  useRecurringGifts,
  editModalPanels,
};

function composeGiftWithEditsFromCurrentGift(currentGift) {
  const amountInDollars = getAmountInDollarsClean(currentGift.amount);

  return {
    ID: currentGift.ID,
    amountInDollars,
    interval: currentGift.interval,
    startDate: currentGift.paymentDates[0]
      ? currentGift.paymentDates[0]
      : formatTimestampToSQLDate(currentGift.startDate),
    endDate: currentGift.endDate
      ? formatTimestampToSQLDate(currentGift.endDate)
      : null,
    paymentMethod: currentGift.paymentMethod,
    applyToExistingPledge: currentGift.applyToExistingPledge,
    memo: currentGift.memo,
  };
}

function composeAddOnGifts(designations = {}) {
  return Object.fromEntries(
    designations
      ?.filter(({ addOnGiftID }) => Boolean(addOnGiftID))
      .map(({ addOnGiftID, amount, ...addOnGift }) => [
        addOnGiftID,
        {
          ...addOnGift,
          amount: getAmountInDollarsAndCents(amount),
          addOnGiftID,
        },
      ])
  );
}

function composeMainGift(designations = {}) {
  const { amount: amountInCents, ...rest } = designations.find(
    ({ addOnGiftID }) => !addOnGiftID // Main gift will not have this ID.
  );
  return { amount: getAmountInDollarsAndCents(amountInCents), ...rest };
}
